import React from "react";
import { Grid } from "@mui/material";
import LabeledNumber from "../../../Widgets/LabeledNumber";
import { TherapyHours } from "./interface";
import { ClientInsurancePlanCptCodes } from "hooks/api/insurance/useGetClientInsurancePlanCptCodes";

interface EditFieldsProps {
  therapyHours: TherapyHours;
  onTherapyHoursChange: (field: keyof TherapyHours, value: number | string) => void;
  cptCodes: ClientInsurancePlanCptCodes | null;
}

const codeLabel = (name: string, info: { code: string } | null): string =>
  info ? `${name} - ${info.code}` : name;

// Payors bill in quarter-hour increments, so recommended hours must land on the 0.25 grid.
// The input's `step` only constrains the spinner arrows, not typed values, so round here too.
const roundToQuarterHour = (value: number): number => Math.round(value * 4) / 4;

export const EditFields: React.FC<EditFieldsProps> = ({
  therapyHours,
  onTherapyHoursChange,
  cptCodes,
}) => {
  const handleNumber =
    (field: keyof TherapyHours) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const parsed = Math.max(0, parseFloat(e.target.value) || 0);
      onTherapyHoursChange(field, roundToQuarterHour(parsed));
    };

  // When cptCodes is provided, only show therapy types that have a mapped code.
  // When cptCodes is null, show all rows with hardcoded labels.
  const showAll = cptCodes === null;

  return (
    <Grid container spacing={7}>
      {(showAll || cptCodes?.assessment) && (
        <LabeledNumber
          label={codeLabel("Assessment", cptCodes?.assessment ?? null)}
          value={therapyHours?.assessment_hours ?? 0}
          onChange={handleNumber("assessment_hours")}
          helperText="Hours each 6 month authorization period"
          min={0}
          step={0.25}
        />
      )}
      {(showAll || cptCodes?.direct_therapy) && (
        <LabeledNumber
          label={codeLabel("Direct Therapy", cptCodes?.direct_therapy ?? null)}
          value={therapyHours?.direct_therapy_hours ?? 0}
          onChange={handleNumber("direct_therapy_hours")}
          helperText="Hours per week"
          min={0}
          step={0.25}
        />
      )}
      {(showAll || cptCodes?.caregiver_mediated) && (
        <LabeledNumber
          label={codeLabel("Caregiver Mediated", cptCodes?.caregiver_mediated ?? null)}
          value={therapyHours?.caregiver_mediated_hours ?? 0}
          onChange={handleNumber("caregiver_mediated_hours")}
          helperText="Hours per week"
          min={0}
          step={0.25}
        />
      )}
      {(showAll || cptCodes?.protocol_modification_direct) && (
        <LabeledNumber
          label={codeLabel(
            "Direct Protocol Modification",
            cptCodes?.protocol_modification_direct ?? null
          )}
          value={therapyHours?.protocol_modification_hours ?? 0}
          onChange={handleNumber("protocol_modification_hours")}
          helperText="Hours per week"
          min={0}
          step={0.25}
        />
      )}
      {(showAll || cptCodes?.protocol_modification_indirect) && (
        <LabeledNumber
          label={codeLabel(
            "Indirect Protocol Modification",
            cptCodes?.protocol_modification_indirect ?? null
          )}
          value={therapyHours?.indirect_protocol_modification_hours ?? 0}
          onChange={handleNumber("indirect_protocol_modification_hours")}
          helperText="Hours per week"
          min={0}
          step={0.25}
        />
      )}
      {(showAll || cptCodes?.caregiver_training) && (
        <LabeledNumber
          label={codeLabel("Caregiver Training", cptCodes?.caregiver_training ?? null)}
          value={therapyHours?.caregiver_training_hours ?? 0}
          onChange={handleNumber("caregiver_training_hours")}
          helperText="Hours per week"
          min={0}
          step={0.25}
        />
      )}
    </Grid>
  );
};

export default EditFields;
